/**
 * useDemoLeadGate — guards high-intent actions (PDF download, print,
 * send) on sandbox sessions. Returns:
 *   - `guard(action)` — call from a click handler. If the user is in a
 *     sandbox AND hasn't captured a lead yet, the wrapped action is
 *     deferred and the lead-capture modal opens. After successful
 *     capture the deferred action fires. Non-sandbox users invoke the
 *     action immediately.
 *   - `modal` — the JSX to mount once near the top of the tree.
 *
 * Co-located with the sandbox session helper so a single shared modal
 * instance covers every gated action across the app.
 */
import React, { useCallback, useRef, useState } from 'react';
import { useSandboxSession } from './useSandboxSession';
import { DemoLeadCaptureModal } from '../components/DemoLeadCaptureModal';

export function useDemoLeadGate() {
  const { isSandbox, leadCaptured } = useSandboxSession();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('unlock document exporting');
  const pendingRef = useRef(null);

  const guard = useCallback(
    (action, opts = {}) => {
      // Non-sandbox or already-captured users → run immediately.
      if (!isSandbox || leadCaptured) {
        action?.();
        return;
      }
      pendingRef.current = action;
      setReason(opts.reason || 'unlock document exporting');
      setOpen(true);
    },
    [isSandbox, leadCaptured],
  );

  const handleSubmitted = useCallback(() => {
    const action = pendingRef.current;
    pendingRef.current = null;
    // Run the deferred action on the next tick so the modal can close
    // cleanly first (otherwise a browser-blocked download dialog can
    // pop while React is still tearing the modal down).
    setTimeout(() => action?.(), 0);
  }, []);

  const modal = (
    <DemoLeadCaptureModal
      open={open}
      onOpenChange={(o) => {
        if (!o) pendingRef.current = null;
        setOpen(o);
      }}
      onSubmitted={handleSubmitted}
      reason={reason}
    />
  );

  return { guard, modal, isSandbox, leadCaptured };
}
